import React from 'react';
import { formatCampaignType, formatEnumPretty, formatBiddingStrategy, formatNetworkSettings } from '@/utils/formatEnum';
import { formatAccountId as formatMetaAccountId } from '@/utils/metaAds';
import { FeedbackCard } from '@/components/features/FeedbackCard';
import { CampaignStatusBadge } from '@/components/features/CampaignCard/shared/CampaignStatusBadge';
import type { Campaign } from '@/types/campaign';
import { isMetaCampaign } from '@/types/campaign';
import type { useFeedback } from '@/hooks/useFeedback';
import { formatRoasDisplay } from '@/utils/roas';

/**
 * Compute the sidebar's "Type" string. Google campaigns use Google Ads
 * campaign type enums; Meta campaigns surface their `objective`.
 */
function getDisplayCampaignType(campaign: Campaign): string {
  if (isMetaCampaign(campaign)) {
    return campaign.objective ? formatEnumPretty(campaign.objective) : 'Meta Ads';
  }
  return formatCampaignType(campaign.campaign_type) || 'N/A';
}

/**
 * Compute the sidebar's "Account" string. Google uses the digits-only
 * 10-character customer ID with dashes; Meta uses the `act_XXX` form.
 */
function getDisplayAccountId(campaign: Campaign): string | null {
  if (isMetaCampaign(campaign)) {
    // Strip the `act_` namespace prefix for display, matching the
    // canonical helper used elsewhere (SettingsPanel, MetaCampaignCard).
    return campaign.meta_ad_account_id
      ? formatMetaAccountId(campaign.meta_ad_account_id)
      : null;
  }
  return campaign.google_ads_account_id
    ? campaign.google_ads_account_id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
    : null;
}

/**
 * Compute the sidebar's "Bidding Strategy" string. For Meta this is
 * `bid_strategy` + `buying_type` (e.g., "Lowest Cost (Auction)").
 */
function getDisplayBidStrategy(campaign: Campaign): { label: string; value: string } | null {
  if (isMetaCampaign(campaign)) {
    const strategy = campaign.bid_strategy ? formatEnumPretty(campaign.bid_strategy) : null;
    if (!strategy) return null;
    const suffix = campaign.buying_type ? ` (${formatEnumPretty(campaign.buying_type)})` : '';
    return { label: 'Bid Strategy', value: `${strategy}${suffix}` };
  }
  if (!campaign.bidding_strategy || campaign.bidding_strategy === 'UNKNOWN') return null;
  const label = campaign.bidding_strategy.includes(',') ? 'Bidding Strategies' : 'Bidding Strategy';
  const value = campaign.bidding_strategy.includes(',')
    ? campaign.bidding_strategy.split(',').map(s => formatBiddingStrategy(s.trim())).join(', ')
    : formatBiddingStrategy(campaign.bidding_strategy);
  return { label, value };
}

interface CampaignSidebarProps {
  campaign: Campaign;
  showStickyName: boolean;
  roas: number | null;
  feedback: ReturnType<typeof useFeedback>;
}

/**
 * Sticky sidebar with campaign details and feedback card.
 */
export function CampaignSidebar({
  campaign,
  showStickyName,
  roas,
  feedback,
}: CampaignSidebarProps) {
  // Resolve the bid-strategy row once per render so the conditional
  // render block doesn't call `getDisplayBidStrategy` three times.
  // Also keeps future edits from drifting if the helper is later made
  // non-trivial.
  const bidStrategy = getDisplayBidStrategy(campaign);
  return (
    <div className="lg:w-72 flex-shrink-0">
      <div className="sticky top-[calc(var(--header-h)_+_1rem)] z-10 space-y-4">
        {/* Campaign Details Card */}
        <div className="bg-gray-50 p-5 rounded-lg border overflow-hidden">
          {/* Dynamic campaign name that slides in when main title scrolls out of view - hidden on mobile/tablet */}
          <div
            className="hidden lg:block transition-all duration-300 ease-in-out border-b"
            style={{
              maxHeight: showStickyName ? '60px' : '0px',
              opacity: showStickyName ? 1 : 0,
              marginBottom: showStickyName ? '16px' : '0px',
              paddingBottom: showStickyName ? '12px' : '0px',
              borderColor: showStickyName ? 'rgb(229, 231, 235)' : 'transparent',
            }}
          >
            <h3 className="text-base font-bold text-gray-900 leading-tight">
              {campaign.campaign_name}
            </h3>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Details</h2>
          {/* Mobile: 2-column grid layout */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm md:hidden">
            {/* Row 1: Type | Account ID */}
            <div>
              <span className="text-gray-500 block">Type</span>
              <span className="font-medium text-gray-900">
                {getDisplayCampaignType(campaign)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 block">
                {isMetaCampaign(campaign) ? 'Meta Ad Account' : 'Account ID'}
              </span>
              <span className="font-medium text-gray-900">
                {getDisplayAccountId(campaign) ?? 'N/A'}
              </span>
            </div>
            {/* Row 2: Status | Bidding Strategy */}
            <div>
              <span className="text-gray-500 block">Status</span>
              <CampaignStatusBadge status={campaign.campaign_status ?? campaign.status} />
            </div>
            <div>
              <span className="text-gray-500 block">
                {campaign.bidding_strategy?.includes(',') ? 'Strategies' : 'Strategy'}
              </span>
              <span className="font-medium text-gray-900">
                {campaign.bidding_strategy && campaign.bidding_strategy !== 'UNKNOWN'
                  ? (campaign.bidding_strategy.includes(',')
                      ? campaign.bidding_strategy.split(',').map(s => formatBiddingStrategy(s.trim())).join(', ')
                      : formatBiddingStrategy(campaign.bidding_strategy))
                  : 'N/A'}
              </span>
            </div>
            {/* Row 3: Daily Budget | ROAS. Both platforms now render
                the same shared `roas` via `formatRoasDisplay` —
                `null` becomes "N/A". */}
            <div>
              <span className="text-gray-500 block">Daily Budget</span>
              <span className="text-xl font-bold text-green-600">
                ${campaign.budget.toFixed(2)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 block">ROAS</span>
              <span className="text-xl font-bold text-green-600">
                {formatRoasDisplay(roas)}
              </span>
            </div>
            {/* Row 4: Targeting & Networks (spans both columns) - Show when any targeting/network info is available */}
            {(campaign.network_settings ||
              (campaign.location_targeting && campaign.location_targeting.length > 0) ||
              (campaign.language_targeting && campaign.language_targeting.length > 0) ||
              (campaign.audience_targeting && campaign.audience_targeting.total_audiences > 0)) && (
              <div className="col-span-2 pt-2 mt-2 border-t border-gray-200">
                <span className="text-gray-500 block mb-1">Targeting & Networks</span>
                <div className="space-y-1">
                  <div>
                    <span className="text-gray-400 text-xs">Locations:</span>
                    <span className="font-medium text-gray-900 text-xs ml-1">
                      {campaign.location_targeting
                        ? (campaign.location_targeting.length > 0
                            ? campaign.location_targeting
                                .filter(loc => !loc.is_negative)
                                .map(loc => loc.target_name)
                                .filter(Boolean)
                                .join(', ') || 'All locations'
                            : 'All locations')
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Languages:</span>
                    <span className="font-medium text-gray-900 text-xs ml-1">
                      {campaign.language_targeting
                        ? (campaign.language_targeting.length > 0
                            ? campaign.language_targeting
                                .map(lang => lang.target_name)
                                .filter(Boolean)
                                .join(', ')
                            : 'All languages')
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Audiences:</span>
                    <span className="font-medium text-gray-900 text-xs ml-1">
                      {campaign.audience_targeting && campaign.audience_targeting.total_audiences > 0
                        ? `${campaign.audience_targeting.total_audiences} segment${campaign.audience_targeting.total_audiences !== 1 ? 's' : ''}`
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Networks:</span>
                    <span className="font-medium text-gray-900 text-xs ml-1">
                      {campaign.network_settings
                        ? formatNetworkSettings(campaign.network_settings).join(', ') || 'None'
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Tablet/Desktop: Original vertical layout */}
          <div className="hidden md:block space-y-3 text-sm">
            <div>
              <span className="text-gray-500 block">Type</span>
              <span className="font-medium text-gray-900">
                {getDisplayCampaignType(campaign)}
              </span>
            </div>
            {getDisplayAccountId(campaign) && (
              <div>
                <span className="text-gray-500 block">
                  {isMetaCampaign(campaign) ? 'Meta Ad Account' : 'Account ID'}
                </span>
                <span className="font-medium text-gray-900">
                  {getDisplayAccountId(campaign)}
                </span>
              </div>
            )}
            {bidStrategy && (
              <div>
                <span className="text-gray-500 block">
                  {bidStrategy.label}
                </span>
                <span className="font-medium text-gray-900">
                  {bidStrategy.value}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500 block">Status</span>
              <CampaignStatusBadge status={campaign.campaign_status ?? campaign.status} />
            </div>
            <div>
              <span className="text-gray-500 block">Daily Budget</span>
              <span className="text-xl font-bold text-green-600">
                ${campaign.budget.toFixed(2)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 block">ROAS</span>
              <span className="text-xl font-bold text-green-600">
                {formatRoasDisplay(roas)}
              </span>
            </div>

            {/* Targeting & Networks Section - Show when any targeting/network info is available */}
            {(campaign.network_settings ||
              (campaign.location_targeting && campaign.location_targeting.length > 0) ||
              (campaign.language_targeting && campaign.language_targeting.length > 0) ||
              (campaign.audience_targeting && campaign.audience_targeting.total_audiences > 0)) && (
              <div className="pt-3 mt-3 border-t border-gray-200">
                <span className="text-gray-500 block mb-2">Targeting & Networks</span>
                <div className="space-y-2">
                  <div>
                    <span className="text-gray-500 block text-xs">Locations</span>
                    <span className="font-medium text-gray-900 text-xs">
                      {campaign.location_targeting
                        ? (campaign.location_targeting.length > 0
                            ? campaign.location_targeting
                                .filter(loc => !loc.is_negative)
                                .map(loc => loc.target_name)
                                .filter(Boolean)
                                .join(', ') || 'All locations'
                            : 'All locations')
                        : 'N/A'}
                    </span>
                    {campaign.location_targeting && campaign.location_targeting.filter(loc => loc.is_negative).length > 0 && (
                      <span className="text-red-600 block text-xs mt-0.5">
                        Excluded: {campaign.location_targeting
                          .filter(loc => loc.is_negative)
                          .map(loc => loc.target_name)
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">Languages</span>
                    <span className="font-medium text-gray-900 text-xs">
                      {campaign.language_targeting
                        ? (campaign.language_targeting.length > 0
                            ? campaign.language_targeting
                                .map(lang => lang.target_name)
                                .filter(Boolean)
                                .join(', ')
                            : 'All languages')
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">Audiences</span>
                    <span className="font-medium text-gray-900 text-xs">
                      {campaign.audience_targeting && campaign.audience_targeting.total_audiences > 0
                        ? `${campaign.audience_targeting.total_audiences} audience segment${campaign.audience_targeting.total_audiences !== 1 ? 's' : ''}`
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">Networks</span>
                    <span className="font-medium text-gray-900 text-xs">
                      {campaign.network_settings
                        ? formatNetworkSettings(campaign.network_settings).join(', ') || 'None'
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Feedback Card - Sticky with Campaign Details, centered and thinner */}
        {feedback.isVisible && feedback.currentQuestion && (
          <div className="hidden md:flex justify-center">
            <div className="w-56">
              <FeedbackCard
                question={feedback.currentQuestion}
                onSubmit={feedback.submitResponse}
                isSubmitting={feedback.isSubmitting}
                error={feedback.error}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
